// ===========================================================================
// PatientHeader — cabecera de la ficha: identidad + 3 chips grandes
// (costo total, Health Status+GI, categoría social).
// ===========================================================================
import React from "react";
import { Box, Paper, Typography, Stack, Chip, Avatar } from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import GroupsIcon from "@mui/icons-material/Groups";
import { formatCurrency } from "../../utils/format";
import { HEALTH_STATUS_COLORS, SOCIAL_CATEGORY_COLORS } from "../../common/constants";

const BigChip = ({ icon, label, value, color }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1, minWidth: 160, borderLeft: `4px solid ${color}` }}>
    <Stack direction="row" spacing={1} alignItems="center">
      {icon}
      <Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

export default function PatientHeader({ header, socialCategory }) {
  if (!header) return null;
  const hsColor = HEALTH_STATUS_COLORS[header.healthStatus] || "#9e9e9e";
  const socColor = SOCIAL_CATEGORY_COLORS[socialCategory] || "#9e9e9e";

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 2.5 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Avatar sx={{ width: 56, height: 56, bgcolor: "primary.main", fontWeight: 700 }}>
          {(header.memberFullName || "?").charAt(0)}
        </Avatar>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {header.memberFullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {header.age}y · {header.gender} · CIP {header.cipId} · {header.pcpName || "Unassigned"}
            {header.pcpTitle ? ` (${header.pcpTitle})` : ""} · {header.medicalCenter || "Unassigned"}
          </Typography>
        </Box>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <BigChip
          icon={<AttachMoneyIcon sx={{ color: "#1976d2" }} />}
          label="Total cost"
          value={formatCurrency(header.totalCost)}
          color="#1976d2"
        />
        <BigChip
          icon={<LocalHospitalIcon sx={{ color: hsColor }} />}
          label="Clinical"
          value={`${header.healthStatus || "—"} · GI ${header.globalIndicator}`}
          color={hsColor}
        />
        <BigChip
          icon={<GroupsIcon sx={{ color: socColor }} />}
          label="Social category"
          value={socialCategory || "Not assessed"}
          color={socColor}
        />
      </Stack>

      {header.weeksAbsent > 0 && (
        <Chip
          label={`Absent from Hability for ${header.weeksAbsent} week(s)`}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ mt: 2 }}
        />
      )}
    </Paper>
  );
}
